import { Badge, HStack, Text, VStack } from "@chakra-ui/react";
import { FC, useMemo } from "react";
import { NavLink } from "react-router-dom";

import {
  TableDataColumn,
  TableDataRow,
} from "~/src/components/common/tables/Table";

import { FunnelUserTagModuleTableActions } from "./FunnelUserTagModuleTableActions";

type UserTagModule = {
  id: string;
  name: string;
  effectiveEnabled: boolean;
  hasPendingToggle: boolean;
  publishedAt: string | null;
  createdAt: string;
  popupGroups: ReadonlyArray<{
    id: string;
    name: string;
    slug: string;
    page: { id: string };
  }>;
  abtestScenarioPageGroups: ReadonlyArray<{
    id: string;
    title: string;
    slug: string;
    page: { id: string };
  }>;
};

type Props = {
  siteSlug: string;
  module: UserTagModule;
  pageId: string | null;
};

const linkedGroupColumnProps = {
  type: "text" as const,
  alignItems: "center" as const,
};

export const FunnelUserTagModuleTableDataRow: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ module, siteSlug, pageId }) => {
  const filteredPopupGroups = useMemo(
    () =>
      pageId
        ? module.popupGroups.filter((group) => group.page.id === pageId)
        : module.popupGroups,
    [module.popupGroups, pageId]
  );

  const filteredAbtestGroups = useMemo(
    () =>
      pageId
        ? module.abtestScenarioPageGroups.filter(
            (group) => group.page.id === pageId
          )
        : module.abtestScenarioPageGroups,
    [module.abtestScenarioPageGroups, pageId]
  );

  const lastUpdatedAt = module.publishedAt ?? module.createdAt;

  return (
    <TableDataRow minHeight="80px" height="auto">
      <TableDataColumn type="text" data={module.name} />
      <TableDataColumn
        type="text"
        data={
          <HStack spacing={2}>
            <Text>{module.effectiveEnabled ? "有効" : "無効"}</Text>
            {module.hasPendingToggle && (
              <Badge colorScheme="blue" size="sm">
                切替予定
              </Badge>
            )}
          </HStack>
        }
      />
      <TableDataColumn
        {...linkedGroupColumnProps}
        data={
          filteredPopupGroups.length > 0 ? (
            <VStack align="start" spacing={0.5}>
              {filteredPopupGroups.map((group) => (
                <NavLink
                  key={group.id}
                  to={`/sites/${siteSlug}/popup_groups/${group.slug}`}
                >
                  {group.name}
                </NavLink>
              ))}
            </VStack>
          ) : (
            "—"
          )
        }
      />
      <TableDataColumn
        {...linkedGroupColumnProps}
        data={
          filteredAbtestGroups.length > 0 ? (
            <VStack align="start" spacing={0.5}>
              {filteredAbtestGroups.map((group) => (
                <NavLink
                  key={group.id}
                  to={`/sites/${siteSlug}/abtests/${group.slug}`}
                >
                  {group.title}
                </NavLink>
              ))}
            </VStack>
          ) : (
            "—"
          )
        }
      />
      <TableDataColumn type="date" data={lastUpdatedAt} />
      <TableDataColumn
        type="actions"
        data={
          <FunnelUserTagModuleTableActions
            siteSlug={siteSlug}
            moduleId={module.id}
          />
        }
      />
    </TableDataRow>
  );
};
